/**
 * Lease-duration helper (T-08): "Total längd: X år Y mån Z dagar".
 *
 * Pure calendar arithmetic over two ISO `YYYY-MM-DD` strings. The contract
 * editor uses it to show a live read-out beneath a `date` field that
 * declares `durationFrom` (its sibling start-date key). Returns null when
 * either bound is missing/unparseable or the range is negative, so the
 * caller renders nothing.
 *
 * The arithmetic is calendar-aware: we count the whole months that fit
 * between the dates (clamping day-of-month on short months, so
 * 31 Jan → 28 Feb is one month, not "one month minus three days"), then
 * the leftover days. This matches how a human reads a lease term and
 * avoids the borrow-from-previous-month bugs of naive field subtraction.
 */
import dayjs from 'dayjs';
import { parseDateValue } from './notification-rules.js';
export function dateRangeParts(startIso, endIso) {
    if (!parseDateValue(startIso) || !parseDateValue(endIso))
        return null;
    const start = dayjs(startIso);
    const endRaw = dayjs(endIso);
    if (!start.isValid() || !endRaw.isValid() || endRaw.isBefore(start))
        return null;
    // Inclusive of both endpoints: a lease running the 1st–31st covers the
    // 31st too, so we count up to the day AFTER the end date. (A start === end
    // single day is therefore "1 dag".) Applied after the negative-range guard
    // so an end before start still returns null.
    const end = endRaw.add(1, 'day');
    // Count whole months that fit (dayjs `.add` clamps day-of-month on short
    // months: 31 Jan + 1 month → 28/29 Feb), then the leftover days.
    let months = end.diff(start, 'month');
    if (start.add(months, 'month').isAfter(end))
        months -= 1;
    const days = end.diff(start.add(months, 'month'), 'day');
    return { years: Math.floor(months / 12), months: months % 12, days };
}
/**
 * Swedish read-out, e.g. "3 år 2 mån 0 dagar". Shows every unit from the
 * highest non-zero one down (so trailing zeros within a term are kept:
 * "1 år 0 mån 0 dagar"), and "0 dagar" for a zero-length range. Returns
 * null when {@link dateRangeParts} does.
 */
export function formatLeaseDuration(startIso, endIso) {
    const parts = dateRangeParts(startIso, endIso);
    if (!parts)
        return null;
    const { years, months, days } = parts;
    const segments = [];
    if (years > 0)
        segments.push(`${years} år`);
    if (segments.length > 0 || months > 0)
        segments.push(`${months} mån`);
    segments.push(`${days} ${days === 1 ? 'dag' : 'dagar'}`);
    return segments.join(' ');
}
//# sourceMappingURL=duration.js.map